import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';

import type { AssetsShareUserSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';

import type { AppState } from '../../store/types';

const getUserLabel = (user: AssetsShareUserSchemaWithId) => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  if (user.first_name) {
    return user.first_name;
  }
  return user.email;
};

const getShareUsers = (state: AppState) => state.sharesUsers;

const getShareUsersEntities = (state: AppState) => state.entities.share_users;

const selectShareUsersIds = createSelector(
  [getShareUsers, (_, shareId) => shareId],
  (shareUsers, shareId) => shareUsers[shareId],
);

export const selectShareUsersByShareId = createCachedSelector(
  getShareUsersEntities,
  (_: AppState, shareId: string) => shareId,
  (shareUsers, shareId) => {
    return Object.values(shareUsers).filter(
      (shareUser) => shareUser.share_id === shareId,
    );
  },
)((_, shareId) => shareId);

export const selectInternalUserIdsOfShareUsers = createSelector(
  getShareUsersEntities,
  selectShareUsersIds,
  (shareUsers, ids) =>
    ids
      ?.map((id) => shareUsers[id])
      .map((user) => user.internal_user_id ?? '')
      .filter((id) => Boolean(id)),
);

/**
  We have two types of users as "share users":
  - Internal users i.e. users registered in our system with registered email,
    photos, first_name and all of this good stuff
  - External users i.e. users that become sort of transient entities only
    in share mechanics when we type email address directly, we only
    know emails of these users
  This condition is just a way to distinguish between this two types of users,
  because for every type we need different set of fields to be able show them
  in select field correctly.
 */
const getShareUserMainInfo = (user: AssetsShareUserSchemaWithId) => {
  if (user.internal_user_id) {
    return {
      label: getUserLabel(user),
      value: user.id,
      email: user.email,
      photo: user.photo,
    };
  }

  return {
    label: user.email,
    value: user.id,
  };
};

const EMPTY_SHARE_USERS_MAIN_INFO: ReturnType<typeof getShareUserMainInfo>[] =
  [];

export const selectShareUsersMainInfo = createSelector(
  getShareUsersEntities,
  selectShareUsersIds,
  (shareUsers, ids) =>
    ids
      ? ids.map((id) => getShareUserMainInfo(shareUsers[id]))
      : EMPTY_SHARE_USERS_MAIN_INFO,
);
